import React, { memo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Campaign, CampaignProspectType } from '../../../api/campaigns';
import { formatLocaleDateTime } from '../../../utils/DateTimeFormatter';
import AvatarGenerator from '../../../components/avatar/avatar';

interface CampaignCardProps {
  campaign: Campaign;
  onDuplicate?: (campaign: Campaign) => void;
  isArchivedView?: boolean;
  onArchive?: (campaign: Campaign, archived: boolean) => void;
}

const CampaignCard: React.FC<CampaignCardProps> = memo(
  ({ campaign, onDuplicate, isArchivedView = false, onArchive }) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const prospectType =
      campaign.prospect_type ||
      (campaign.name.toLowerCase().includes('account')
        ? CampaignProspectType.ACCOUNT
        : CampaignProspectType.OPPORTUNITY);

    const isAccountCampaign = prospectType === CampaignProspectType.ACCOUNT;
    const campaignTypeLabel = isAccountCampaign ? 'Account' : 'Opportunity';
    const IconComponent = isAccountCampaign ? BusinessIcon : AssignmentIcon;

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setMenuAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
      setMenuAnchorEl(null);
    };

    const handleDuplicateClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleMenuClose();
      onDuplicate?.(campaign);
    };

    const handleArchiveClick = (e: React.MouseEvent, archived: boolean) => {
      e.stopPropagation();
      handleMenuClose();
      onArchive?.(campaign, archived);
    };

    const showMenu = isArchivedView
      ? Boolean(onArchive)
      : onDuplicate != null || onArchive != null;

    const cardLinkProps = isArchivedView
      ? {}
      : {
          component: RouterLink,
          to: `/campaign/${campaign.id}`
        };

    return (
      <Card
        {...cardLinkProps}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          cursor: isArchivedView ? 'default' : 'pointer',
          textDecoration: 'none',
          color: 'inherit',
          '&:hover': isArchivedView
            ? undefined
            : {
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
              }
        }}
      >
        <CardContent sx={{ flexGrow: 1, padding: '20px' }}>
          {/* Header with Icon and Type Badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}
          >
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: (theme) => theme.customColors.themeTertiary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <IconComponent
                sx={{
                  fontSize: '28px',
                  color: (theme) =>
                    isAccountCampaign
                      ? theme.customColors.spreadsheetAccountAccent
                      : theme.customColors.spreadsheetOpportunityAccent
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={campaignTypeLabel}
                size="small"
                sx={{
                  backgroundColor: (theme) =>
                    `${isAccountCampaign ? theme.customColors.spreadsheetAccountAccent : theme.customColors.spreadsheetOpportunityAccent}15`,
                  color: (theme) =>
                    isAccountCampaign
                      ? theme.customColors.spreadsheetAccountAccent
                      : theme.customColors.spreadsheetOpportunityAccent,
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  height: '24px',
                  textTransform: 'capitalize'
                }}
              />
              {showMenu && (
                <>
                  <IconButton
                    size="small"
                    aria-label="Campaign actions"
                    onClick={handleMenuOpen}
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: (theme) =>
                          `${isAccountCampaign ? theme.customColors.spreadsheetAccountAccent : theme.customColors.spreadsheetOpportunityAccent}15`,
                        color: (theme) =>
                          isAccountCampaign
                            ? theme.customColors.spreadsheetAccountAccent
                            : theme.customColors.spreadsheetOpportunityAccent
                      }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isArchivedView ? (
                      <MenuItem onClick={(e) => handleArchiveClick(e, false)}>
                        <ListItemIcon>
                          <UnarchiveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Unarchive campaign</ListItemText>
                      </MenuItem>
                    ) : (
                      <>
                        {onDuplicate && (
                          <MenuItem onClick={handleDuplicateClick}>
                            <ListItemIcon>
                              <ContentCopyIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Duplicate campaign</ListItemText>
                          </MenuItem>
                        )}
                        {onArchive && (
                          <MenuItem
                            onClick={(e) => handleArchiveClick(e, true)}
                          >
                            <ListItemIcon>
                              <ArchiveIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Archive campaign</ListItemText>
                          </MenuItem>
                        )}
                      </>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>

          {/* Campaign Name */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: '600',
              color: (theme) => theme.customColors.themeNeutralPrimaryColor,
              marginBottom: '12px',
              fontSize: '1.1rem',
              lineHeight: 1.3
            }}
          >
            {campaign.name}
          </Typography>

          {/* Description */}
          {campaign.description && (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                marginBottom: '20px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.5,
                minHeight: '40px'
              }}
            >
              {campaign.description}
            </Typography>
          )}

          {/* Creator Info */}
          {campaign.creator_name && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                marginBottom: '12px'
              }}
            >
              <AvatarGenerator fullname={campaign.creator_name} size={24} />
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                {campaign.creator_name}
              </Typography>
            </Box>
          )}

          {/* Created Date */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              marginTop: 'auto'
            }}
          >
            <AccessTimeIcon
              sx={{
                fontSize: '14px',
                color: (theme) => theme.palette.text.secondary
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                fontSize: '0.75rem'
              }}
            >
              {formatLocaleDateTime(campaign.create_ts)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }
);

CampaignCard.displayName = 'CampaignCard';

export default CampaignCard;
